import { ImageAssetSettings, VideoAssetSettings } from '@/features/ai-video-creator/stores/productMediaSlice';
import { z } from 'zod';
import { TryOnAvatar } from '@/features/ai-try-on/types';
import { StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';

export type UploadNewMediaOptions = {
  id: string;
  baseFolder: string;
  fileExtension?: string;
};

export enum GenerationType {
  Video = 'video',
  Image = 'image',
  Background = 'background',
  Avatar = 'avatar',
}

export enum PlacementType {
  Automatic = 'automatic',
  ManualPlacement = 'manual_placement',
  ManualPadding = 'manual_padding',
}

export enum ImageProviders {
  stock = 'stock',
  library = 'library',
  instagram = 'instagramGraph',
  googleDrive = 'googleDrive',
  dropbox = 'dropbox',
  product = 'product',
  uploaded = 'uploaded',
  aiGenerated = 'aiGenerated',
  google = 'google',
}

export enum VideoProviders {
  aiGenerated = 'aiGenerated',
}

export enum StabilityAIAspectRatio {
  _1x1 = '1:1',
  _16x9 = '16:9',
  _21x9 = '21:9',
  _2x3 = '2:3',
  _3x2 = '3:2',
  _4x5 = '4:5',
  _5x4 = '5:4',
  _9x16 = '9:16',
  _9x21 = '9:21',
}

export enum MediaAssetSource {
  generated = 'generated',
  uploaded = 'uploaded',
}

export enum MediaAssetType {
  image = 'image',
  video = 'video',
  audio = 'audio',
}

export type SourceMediaAsset = {
  type: MediaAssetType;
  height?: number;
  width?: number;
  src: string;
  previewImage?: string;
};

export type MediaAsset = SourceMediaAsset & {
  id: string;
  alt?: string;
  name?: string;
  status?: string;
  createdAt?: string | null;
  source?: string;
  provider?: string;
  settings?: VideoAssetSettings | ImageAssetSettings;
  generationConfiguration?: CombinedGenerationConfiguration;
  createdWith?: AssetCreationContext;
};

export type ICardMediaAsset = {
  id: string;
  src?: string;
  type?: MediaAssetType;
  status?: string;
  createdAt?: string | null;
  name?: string;
  result?: string;
  alt?: string;
  source?: string;
  previewImage?: string;
  purpose?: MediaAssetPurpose;
};

export type Image = Omit<MediaAsset, 'type'>;

export interface ImageWithProductId extends Image {
  productId?: string;
}

export enum PlayAppMediaAssetStatus {
  Uploading = 'uploading',
  Processing = 'processing',
  Done = 'done',
  Invalid = 'invalid',
  Migrating = 'migrating',
}

export enum MediaAssetStatus {
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

export enum AssetCreationName {
  studioVideo = 'studio_video',
}

export enum AssetCreationToolName {
  tryOnProductImage = 'try_on_product_image',
  tryOnAvatar = 'try_on_avatar',
  personalAvatarAiImage = 'personal_avatar_image',
  animateImage = 'animate-image',
  textToImage = 'text_to_image',
  textToVideo = 'text_to_video',
  replaceImageBackground = 'replace_image_background',
  chatToImage = 'chat_to_image',
  backgroundAudio = 'background_audio',
  manualUpload = 'manual_upload',
  studioVideoComposition = 'studio_video_composition',
}

export enum ToolUsageLabels {
  suggested = 'suggested',
  autoGenerated = 'auto_generated',
}

export enum GenerationInstrument {
  textToImage = 'text_to_image',
  textToVideo = 'text_to_video',
  textToAudio = 'text_to_audio',
  imageToImage = 'image_to_image',
  imageToVideo = 'image_to_video',
  tryOnImage = 'try_on_image',
  chatToImage = 'chat_to_image',
}

export const AssetCreationContextSchema = z.object({
  toolName: z.nativeEnum(AssetCreationToolName),
  labels: z.array(z.nativeEnum(ToolUsageLabels)),
});
export type AssetCreationContext = z.infer<typeof AssetCreationContextSchema>;

export enum MediaAssetPurpose {
  tryOnAvatar = 'try_on_avatar',
  personalAvatarImage = 'personal_avatar_image',
  generalPurpose = 'general_purpose',
}

// TODO: rename to StudioMediaAsset
export type StandaloneMediaAsset = {
  appKey: string;
  id: string;
  status: MediaAssetStatus;
  type: MediaAssetType;
  source: MediaAssetSource;
  autogenerated: boolean;
  createdAt: string;
  updatedAt: string;
  productId?: string;
  name: string;
  result?: string;
  durationMS?: number;
  purpose: MediaAssetPurpose;
  instrumentsUsed?: GenerationInstrument[];
  createdWith: AssetCreationContext;
  generationConfiguration?: CombinedGenerationConfiguration;
  error?: string;
  instrumentsWithModifiers?: {
    instrument: GenerationInstrument;
    provider: VideoProviders | ImageProviders;
  }[];
  metadata?: Record<string, string>;
};

export type CompletedStandaloneMediaAsset = StandaloneMediaAsset & {
  status: MediaAssetStatus.DONE;
  result: string;
  src: string;
};

export enum MediaAssetImageProvider {
  BRIA = 'bria',
  STABILITY_AI = 'stability_ai',
}

export enum MediaAssetVideoProvider {
  /** @deprecated runway quality is not good enough */
  RUNWAY = 'runway_ml',
  KLING = 'kling',
}

export enum TryOnProvider {
  KLING = 'kling_fal',
  FASHN_HQ = 'fashn_high_quality',
  // less iterations, faster
  FASHN_FAST = 'fashn',
}

export enum PlayType {
  LOOP = 'loop',
  TIMED = 'timed',
}

export type GeneratedMediaAsset = CompletedStandaloneMediaAsset | StandaloneMediaAsset | ICardMediaAsset;

export type GeneratedMediaAssetWithAvatar = GeneratedMediaAsset & {
  avatar: TryOnAvatar | CompletedStandaloneMediaAsset;
};

export interface BaseStabilityAIConfig {
  provider: 'stability_ai';
  model: string;
  seed: number;
}

export interface TextToImageStabilityConfig extends BaseStabilityAIConfig {
  imagePrompt: string;
  aspectRatio: StabilityAIAspectRatio;
}

export interface ReplaceBackgroundStabilityConfig extends BaseStabilityAIConfig {
  image: string;
  backgroundPrompt?: string;
  backgroundReferenceImage?: string;
  aspectRatio?: StabilityAIAspectRatio;
  placementType?: PlacementType;
}

export interface TolstoyTryonConfig {
  provider: TryOnProvider;
  customerImage: string;
  productImage: string;
}

export interface RunwayConfig {
  image: string;
  videoPrompt: string;
  backgroundPrompt?: string;
  provider: 'runway_ml';
  model: string;
  endFrame?: string;
  duration?: string;
}

export interface KlingConfig {
  image: string;
  videoPrompt: string;
  backgroundPrompt?: string;
  endFrame?: string;
  provider: 'kling';
  model: string;
  mode: string;
  negativePrompt?: string;
  duration?: string;
}

export interface LumaLabsConfig {
  videoPrompt: string;
  provider: 'lumalabs';
  model: string;
}

export type VideoGenerationConfiguration = RunwayConfig | KlingConfig | LumaLabsConfig;
export type ImageGenerationConfiguration =
  | TextToImageStabilityConfig
  | ReplaceBackgroundStabilityConfig
  | TolstoyTryonConfig;

export type MediaAssetGenerationConfiguration = VideoGenerationConfiguration | ImageGenerationConfiguration;

export type KeysOfUnion<T> = T extends any ? keyof T : never;

export type CombinedGenerationConfiguration = {
  [K in KeysOfUnion<MediaAssetGenerationConfiguration>]?: Extract<
    MediaAssetGenerationConfiguration,
    { [P in K]: any }
  >[K];
};
